import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDateTimePicker as DateTimePicker } from '../../../shared/components/forms/HotelDatePicker';
import { addHallBookingPayment } from '../api';
import { HALL_EVENT_PAYMENT_METHOD_OPTIONS } from '../utils/hallEventForm';
import {
  formatHallCurrency,
  paymentModeLabel,
  paymentStatusMeta,
  resolveHallPaymentSummary,
  roundHallMoney,
} from '../utils/hallFinancials';
import {
  getHallDatePickerSlotProps,
  getHallDrawerPaperSx,
  getHallFormInputSx,
} from '../utils/hallDrawerStyles';

const HALL_ACCENT = '#4ce1b6';

function SummaryRow({ label, value, isDark, emphasize = false, muted = false, valueColor }) {
  const labelColor = muted
    ? (isDark ? '#64748b' : '#94a3b8')
    : (isDark ? '#94a3b8' : '#64748b');
  const defaultValueColor = emphasize
    ? (isDark ? '#f8fafc' : '#0f172a')
    : (isDark ? '#e2e8f0' : '#0f172a');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1.5,
        py: emphasize ? 0.55 : 0.35,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: labelColor,
          fontWeight: emphasize ? 800 : 600,
          fontSize: emphasize ? '0.78rem' : '0.72rem',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: emphasize ? 800 : 700,
          fontSize: emphasize ? '0.95rem' : '0.84rem',
          color: valueColor || defaultValueColor,
          flexShrink: 0,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function HallAddPaymentDrawer({
  open,
  onClose,
  bookingId,
  booking,
  payload,
  isDark,
  onPaymentAdded,
}) {
  const summary = useMemo(
    () => resolveHallPaymentSummary(payload, booking),
    [payload, booking],
  );
  const statusMeta = paymentStatusMeta(summary.payment_status, isDark);
  const inputSx = getHallFormInputSx(isDark);
  const dueAmount = roundHallMoney(summary.due_amount);
  const hasDue = dueAmount > 0.009;
  const showExtras = Number(summary.extras_total || 0) > 0;
  const showDiscount = Number(summary.discount_amount || 0) > 0;
  const showGst = Number(summary.gst_amount || 0) > 0;

  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [paidAt, setPaidAt] = useState(() => moment());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setAmount(dueAmount > 0 ? String(dueAmount) : '');
    setMode('cash');
    setPaidAt(moment());
    setReference('');
    setNote('');
    setError('');
    setSubmitting(false);
  }, [open, dueAmount]);

  const handleSubmit = async () => {
    const paymentAmount = roundHallMoney(amount);
    if (paymentAmount <= 0) {
      setError('Enter a payment amount greater than zero.');
      return;
    }
    if (!bookingId) {
      setError('Booking is missing.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await addHallBookingPayment(bookingId, {
        amount: paymentAmount,
        mode,
        paid_at: paidAt?.isValid?.() ? paidAt.toISOString() : moment().toISOString(),
        reference: String(reference || '').trim(),
        note: String(note || '').trim(),
      });
      onPaymentAdded?.(result);
      onClose?.();
    } catch (err) {
      setError(err?.userMessage || err?.response?.data?.amount || err?.message || 'Failed to add payment');
    } finally {
      setSubmitting(false);
    }
  };

  const payments = summary.financial_info.payments || [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{ sx: getHallDrawerPaperSx(isDark) }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
            <PaymentsIcon sx={{ color: HALL_ACCENT }} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                Event Payment
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                Collect pending amount · {booking?.event_name || 'Hall event'} · {booking?.booking_code || booking?.event_id || ''}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.25, py: 2, flex: 1, overflow: 'auto' }}>
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.28) : alpha(HALL_ACCENT, 0.35)}`,
              backgroundColor: isDark ? alpha('#0f172a', 0.55) : '#fff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.25,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}
              >
                Payment Summary
              </Typography>
              <Chip
                size="small"
                label={statusMeta.label}
                sx={{ fontWeight: 700, color: statusMeta.color, bgcolor: statusMeta.bg }}
              />
            </Box>

            <SummaryRow
              label="Hall amount"
              value={formatHallCurrency(summary.base_amount)}
              isDark={isDark}
            />
            {showExtras ? (
              <SummaryRow
                label="Extra charges"
                value={formatHallCurrency(summary.extras_total)}
                isDark={isDark}
              />
            ) : null}
            {showDiscount ? (
              <SummaryRow
                label="Discount"
                value={`−${formatHallCurrency(summary.discount_amount)}`}
                isDark={isDark}
                valueColor={isDark ? '#fca5a5' : '#b91c1c'}
              />
            ) : null}
            {showGst ? (
              <SummaryRow
                label={`Event GST (${summary.gst_percent}%)`}
                value={formatHallCurrency(summary.gst_amount)}
                isDark={isDark}
                muted
              />
            ) : null}

            <Divider sx={{ my: 1, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />

            <SummaryRow
              label="Grand total"
              value={formatHallCurrency(summary.grand_total)}
              isDark={isDark}
              emphasize
            />
            <SummaryRow
              label="Paid"
              value={formatHallCurrency(summary.paid_amount)}
              isDark={isDark}
              valueColor={isDark ? '#86efac' : '#166534'}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
                mt: 1,
                p: 1.1,
                borderRadius: 1.5,
                backgroundColor: hasDue
                  ? (isDark ? 'rgba(255, 152, 0, 0.12)' : 'rgba(255, 152, 0, 0.08)')
                  : (isDark ? 'rgba(76, 175, 80, 0.12)' : 'rgba(76, 175, 80, 0.08)'),
                border: `1px solid ${
                  hasDue
                    ? (isDark ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.22)')
                    : (isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.22)')
                }`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: isDark ? '#cbd5e1' : '#475569',
                  fontSize: '0.78rem',
                }}
              >
                Due
              </Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1rem',
                  color: hasDue
                    ? (isDark ? '#ffb74d' : '#e65100')
                    : (isDark ? '#86efac' : '#2e7d32'),
                }}
              >
                {formatHallCurrency(dueAmount)}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={1.75}>
            <TextField
              label="Payment amount"
              type="number"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: <InputAdornment position="start">INR</InputAdornment>,
              }}
              helperText={dueAmount > 0 ? `Outstanding due: ${formatHallCurrency(dueAmount)}` : 'No outstanding due'}
            />

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                label="Paid at"
                value={paidAt}
                onChange={(value) => setPaidAt(value)}
                slotProps={getHallDatePickerSlotProps(isDark, { fullWidth: true, sx: inputSx })}
              />
            </LocalizationProvider>

            <Box>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}>
                Payment mode
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {HALL_EVENT_PAYMENT_METHOD_OPTIONS.map((option) => {
                  const selected = mode === option.value;
                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={() => setMode(option.value)}
                      sx={{
                        fontWeight: 700,
                        cursor: 'pointer',
                        color: selected ? '#04120e' : (isDark ? '#cbd5e1' : '#334155'),
                        bgcolor: selected
                          ? HALL_ACCENT
                          : (isDark ? alpha('#94a3b8', 0.14) : alpha('#64748b', 0.1)),
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            <TextField
              label="Reference / txn id"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={inputSx}
            />
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mt: 1.75, borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          {payments.length > 0 ? (
            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 1.5, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: isDark ? '#f8fafc' : '#0f172a' }}>
                Recent payments
              </Typography>
              <Stack spacing={1}>
                {[...payments].reverse().slice(0, 6).map((payment) => (
                  <Box
                    key={payment.id}
                    sx={{
                      p: 1.2,
                      borderRadius: 1.5,
                      border: `1px solid ${isDark ? alpha('#64748b', 0.35) : alpha('#cbd5e1', 0.7)}`,
                      backgroundColor: isDark ? alpha('#0f172a', 0.45) : alpha('#fff', 0.95),
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={{ fontWeight: 800, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                        {formatHallCurrency(payment.amount)}
                      </Typography>
                      <Chip
                        size="small"
                        label={paymentModeLabel(payment.mode)}
                        sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem' }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      {payment.paid_at ? moment(payment.paid_at).format('DD MMM YYYY, hh:mm A') : '—'}
                      {payment.reference ? ` · ${payment.reference}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            px: 2.25,
            py: 1.5,
            borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            fullWidth
            sx={{ textTransform: 'none', fontWeight: 700, flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              flex: 1,
              backgroundColor: HALL_ACCENT,
              color: '#04120e',
              '&:hover': { backgroundColor: HALL_ACCENT, opacity: 0.92 },
            }}
          >
            {submitting ? 'Saving...' : 'Record payment'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
